import { EmbeddedPreviewState } from "@intelligems/ig-types";
import { EmbeddedMod } from "@src/store/slices/experienceBuilder/types";
import { createSelector } from "@reduxjs/toolkit";
import { experienceBuilderAiSlice } from "@src/store/slices/experienceBuilder/ai/slice";
import { getVariationDraft } from "@src/store/slices/experienceBuilder/common/selectors/getVariationDraft";
import { getVisibleVariationMods } from "@src/store/slices/experienceBuilder/common/selectors/getVisibleVariationMods";
import { fromMods } from "@src/store/slices/experienceBuilder/common/utils/fromMods";
import { getModErrors } from "@src/store/slices/experienceBuilder/common/utils/getModErrors";
import { normalizeMod } from "@src/store/slices/experienceBuilder/common/utils/normalizeMod";
import { getInsertPlaceholder } from "@src/store/slices/experienceBuilder/component/selectors/getInsertPlaceholder";
import { selectedExperience } from "@src/store/slices/experiences";
import { createObjectHash } from "@src/utils/createHash";

export const getEmbeddedState = createSelector(
  selectedExperience,
  getVisibleVariationMods,
  getVariationDraft,
  getInsertPlaceholder,
  experienceBuilderAiSlice.selectors.getIsAiBusy,
  (
    experience,
    visibleMods,
    draft,
    placeholder,
    isAiBusy
  ): EmbeddedPreviewState => {
    // Copied, not aliased: `getVisibleVariationMods` is memoized, so pushing
    // the draft onto it directly would mutate the selector's cached array.
    const mods = [...visibleMods];
    // Suspend draft preview while AI is busy: AI verifies against the saved
    // mod set, and a draft mod targeting the same selector would otherwise
    // shadow what AI just applied. The draft state stays intact in Redux —
    // it re-applies automatically once `isAiBusy` flips back to false.
    if (!isAiBusy && draft && draft.type !== "injection") {
      mods.push(draft);
    }

    const map = new Map<string, EmbeddedMod>();
    for (const mod of mods) {
      const normalized = normalizeMod(mod);
      const errors = getModErrors(normalized);
      if (Object.keys(errors).length === 0) {
        map.set(normalized.id, normalized);
      }
    }
    const data = fromMods(Array.from(map.values()));
    // The insert placeholder is part of the draft editing UX; suspend it
    // alongside the draft itself when AI is busy.
    if (placeholder && !isAiBusy) {
      data.onsiteEdits.push(placeholder);
    }
    return {
      hashes: createHashes(data),
      experienceId: experience?.id ?? "preview",
      ...data,
    };
  }
);

function createHashes(data: ReturnType<typeof fromMods>) {
  const jsInjections: unknown[] = [];
  for (const injection of data.onsiteInjections) {
    if (injection.customJs) {
      jsInjections.push(injection);
    }
  }
  return {
    mods: createObjectHash(data),
    reload: createObjectHash(jsInjections),
  };
}
